import React from 'react';
import clsx from 'clsx';
import { useFocusable, getCurrentFocusKey } from '@noriginmedia/norigin-spatial-navigation';
import { getNPVRCardFocusKey, getNPVRActionFocusKey } from './npvr-helpers';
import Typography from '@smart-tv/components/typography';
import Button from '@smart-tv/components/button/button';
import ProgressBar from '@smart-tv/components/progress-bar/progress-bar';
import ProgressSpinner from '@smart-tv/components/progress-spinner/progress-spinner';
import PlayIcon from '@smart-tv/components/icons/play';
import { ClockNineInfo } from '@smart-tv/components/icons/clock-nine-info';
import { PlaySentSending } from '@smart-tv/components/icons/play-sent-sending';
import { CheckCircleOutlinedIcon } from '@smart-tv/components/icons/check-circle';
import Image from '@smart-tv/components/image';
import styles from './npvr-card.module.scss';
import { useSetAtom } from 'jotai';
import { focusKeyPrevAtom } from '@smart-tv/atoms';
import { useVodPlayer } from '../player/vod-wrapper/_hooks/use-vod-player';
import { StreamOptions } from '../player/vod-wrapper/_contexts/stream-player-context';

export interface NpvrCardAction {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
}

export interface NpvrCardProps {
  listRef: React.RefObject<HTMLDivElement>;
  headline: string;
  metaLine?: string;
  duration?: string;
  durationInSeconds?: number;
  dateLabel?: string;
  expiryLabel?: string;
  coverUrl?: string;
  providerIconUrl?: string;
  seriesRecording?: boolean;
  seriesId?: number;
  progress?: number;
  action?: NpvrCardAction;
  index: number;
  errorMessageText?: string;
  errorMessageBadge?: string;
  successMessage?: string;
  streamOptions?: StreamOptions;
  assetPressHandler?: () => void;
}

// Presentational recording card - data shape will firm up once the NPVR API contract is defined.
export const NpvrCard: React.FC<NpvrCardProps> = (props) => {
  const {
    listRef,
    headline,
    metaLine,
    duration,
    dateLabel,
    expiryLabel,
    coverUrl,
    providerIconUrl,
    seriesRecording = false,
    progress,
    action,
    errorMessageText,
    errorMessageBadge,
    successMessage,
    streamOptions,
    assetPressHandler
  } = props;
  const setFocusKeyPrev = useSetAtom(focusKeyPrevAtom);
  const { handleOpen } = useVodPlayer();
  const playable = !!streamOptions;

  const handleFocus = React.useCallback(
    ({ node }: { node: HTMLElement }) => {
      if (!listRef.current) return;

      listRef.current.scrollLeft = Math.max(0, node.offsetLeft - listRef.current.clientWidth / 3);
    },
    [listRef]
  );

  const onPlay = () => {
    if (streamOptions) {
      setFocusKeyPrev({ focusKeyPrev: getCurrentFocusKey(), isLiveTV: false });
      handleOpen(streamOptions);
    }
  };

  const { ref, focused } = useFocusable({
    onFocus: handleFocus,
    onEnterPress: playable ? onPlay : assetPressHandler,
    focusKey: getNPVRCardFocusKey(props.index)
  });

  return (
    <div className={clsx(styles['npvr-card'], { [styles['npvr-card--playable']]: playable })}>
      <div
        ref={ref}
        className={clsx(styles['npvr-card__image-wrapper'], {
          [styles['npvr-card__image-wrapper--focused']]: focused
        })}
        onClick={playable ? onPlay : assetPressHandler}
      >
        <Image className={styles['npvr-card__image']} src={coverUrl} />
        {seriesRecording && (
          <Typography variant="caption" className={styles['npvr-card__series-tag']}>
            Serieoptagelse
          </Typography>
        )}
        {providerIconUrl && (
          <div
            className={styles['npvr-card__provider-icon']}
            style={{ ['--image-src' as string]: `url(${providerIconUrl})` }}
          />
        )}
        {playable && (
          <div className={styles['npvr-card__play-icon']}>
            <PlayIcon size="lg" />
          </div>
        )}
        {typeof progress === 'number' && (
          <ProgressBar className={styles['npvr-card__progress-bar']} value={progress} />
        )}
      </div>

      <div className={styles['npvr-card__detail']}>
        <Typography variant="caption" className={styles['npvr-card__title']}>
          {headline}
        </Typography>

        {metaLine && (
          <Typography variant="caption" className={styles['npvr-card__meta']}>
            {metaLine}
          </Typography>
        )}

        {duration && (
          <Typography variant="caption" className={styles['npvr-card__duration']}>
            {duration}
          </Typography>
        )}

        {(dateLabel || expiryLabel) && (
          <div className={styles['npvr-card__info-row']}>
            {dateLabel && (
              <span className={styles['npvr-card__info-item']}>
                <PlaySentSending size="xl" />
                <Typography variant="caption">{dateLabel}</Typography>
              </span>
            )}
            {expiryLabel && (
              <span className={styles['npvr-card__info-item']}>
                <ClockNineInfo size="xl" />
                <Typography variant="caption">{expiryLabel}</Typography>
              </span>
            )}
          </div>
        )}
      </div>

      <div className={styles['npvr-card__footer']}>
        {errorMessageBadge && (
          <div className={`${styles['npvr-card__error']} ${styles['npvr-card__error--badge']}`}>
            <Typography variant="caption" className={styles['npvr-card__error-text']}>
              {errorMessageBadge}
            </Typography>
          </div>
        )}

        {errorMessageText && (
          <div className={`${styles['npvr-card__error']} ${styles['npvr-card__error--text']}`}>
            <Typography variant="caption" className={styles['npvr-card__error-label']}>
              Fejl:
            </Typography>
            <br />
            <Typography variant="caption" className={styles['npvr-card__error-text']}>
              {errorMessageText}
            </Typography>
          </div>
        )}

        {action && !successMessage && (
          <Button
            className={styles['npvr-card__action']}
            variant="button"
            disabled={action.disabled || action.loading}
            onClick={action.onClick}
            onNavFocus={handleFocus}
            icon={action.loading ? <ProgressSpinner icon={true} /> : action.icon ?? ''}
            focusKey={getNPVRActionFocusKey(props.index)}
          >
            {!action.loading && action.label}
          </Button>
        )}

        {successMessage && (
          <div className={styles['npvr-card__success']}>
            <CheckCircleOutlinedIcon size="lg" color="success" />
            <Typography variant="caption2" className={styles['npvr-card__success-text']}>
              {successMessage}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default NpvrCard;
